import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Telescope, Play, Newspaper, Headphones, Sparkles, Loader2, Lock, RotateCcw, Send, ExternalLink, History, Share2, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AiThinking } from "@/components/AiThinking";

const MAX = 200;

const SEARCH_URL = {
  video: (q) => `https://www.youtube.com/results?search_query=${encodeURIComponent(q)}`,
  article: (q) => `https://www.google.com/search?q=${encodeURIComponent(q)}`,
  podcast: (q) => `https://open.spotify.com/search/${encodeURIComponent(q)}`,
};

export default function Explorateur({ onExplore }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const [history, setHistory] = useState([]);
  const [sharing, setSharing] = useState(false);

  const ex = t("atelier.exp", { returnObjects: true }) || {};
  const presets = Array.isArray(ex.presets) ? ex.presets : [];

  const loadHistory = () => {
    api.get("/expedition/history").then(({ data }) => setHistory(data.items || [])).catch(() => {});
  };
  useEffect(() => { loadHistory(); }, []);

  const run = async (value) => {
    const q = ((value ?? topic) || "").trim();
    if (!q || loading) return;
    setTopic(q);
    setLoading(true);
    setData(null);
    setLocked(false);
    try {
      const { data: res } = await api.post("/expedition/discover", { topic: q, lang: (i18n.language || "fr").slice(0, 2) });
      setData(res);
      loadHistory();
    } catch (e) {
      if (e.response?.status === 402) setLocked(true);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || ex.error);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setData(null); setTopic(""); };
  const deepen = () => {
    if (!data || !onExplore) return;
    onExplore(t("atelier.exp.deepenPrompt", { topic: data.topic }));
  };
  const shareCard = async () => {
    if (!data || sharing) return;
    setSharing(true);
    try {
      const counts = `${data.videos.length} ${ex.nounVideos} · ${data.articles.length} ${ex.nounArticles} · ${data.podcasts.length} ${ex.nounPodcasts}`;
      const label = (topic || data.topic || "").trim();
      const { data: blob } = await api.post("/expedition/card", {
        lang: (i18n.language || "fr").slice(0, 2),
        topic: label.charAt(0).toUpperCase() + label.slice(1),
        intro: data.intro || "", counts,
        eyebrow: ex.eyebrow, title_label: ex.cardTitle, cta: ex.cardCta,
      }, { responseType: "blob" });
      const file = new File([blob], "cocoon-expedition.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: ex.title });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "cocoon-expedition.png"; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(ex.cardError);
    } finally {
      setSharing(false);
    }
  };

  const sections = data ? [
    { kind: "video", label: ex.videos, cta: ex.watch, Icon: Play, subKey: "creator", cls: "border-primary/25 bg-primary/[0.06]", chip: "bg-primary/15 text-primary", name: "text-primary", items: data.videos },
    { kind: "article", label: ex.articles, cta: ex.read, Icon: Newspaper, subKey: "source", cls: "border-emerald-300/25 bg-emerald-300/[0.05]", chip: "bg-emerald-300/12 text-emerald-300", name: "text-emerald-300", items: data.articles },
    { kind: "podcast", label: ex.podcasts, cta: ex.listen, Icon: Headphones, subKey: "show", cls: "border-sky-300/25 bg-sky-300/[0.05]", chip: "bg-sky-300/12 text-sky-300", name: "text-sky-300", items: data.podcasts },
  ] : [];

  return (
    <div className="h-full overflow-y-auto scroll-thin">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.3em] text-primary/70 mb-3">
            <Telescope size={14} /> {ex.eyebrow}
          </span>
          <h1 className="font-display text-3xl sm:text-4xl text-gradient-orange leading-tight mb-3" data-testid="exp-title">{ex.title}</h1>
          <p className="text-foreground/60 leading-relaxed max-w-xl mx-auto text-sm sm:text-base">{ex.subtitle}</p>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); run(); }} className="rounded-2xl bg-white/[0.03] border border-white/12 p-3 mb-3" data-testid="exp-form">
          <div className="flex items-center gap-2 px-2">
            <input
              value={topic}
              onChange={(e) => setTopic(e.target.value.slice(0, MAX))}
              placeholder={ex.placeholder}
              data-testid="exp-input"
              className="flex-1 bg-transparent py-2.5 text-sm sm:text-base text-foreground placeholder:text-foreground/35 focus:outline-none"
            />
            <button type="submit" disabled={loading || !topic.trim()} data-testid="exp-submit"
              className="inline-flex items-center gap-2 rounded-full bg-primary/15 border border-primary/40 text-primary px-5 py-2.5 text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-50 whitespace-nowrap">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} {ex.go}
            </button>
          </div>
        </form>

        {!data && !loading && !locked && (
          <div className="mb-6" data-testid="exp-presets">
            <p className="text-xs text-foreground/40 mb-2.5">{ex.suggest}</p>
            <div className="flex flex-wrap gap-2">
              {presets.map((p, i) => (
                <button key={i} onClick={() => run(p)} data-testid={`exp-preset-${i}`}
                  className="text-xs sm:text-sm px-3.5 py-2 rounded-full border border-primary/20 text-foreground/70 hover:text-primary hover:bg-primary/[0.06] hover:border-primary/40 transition-colors">
                  {p}
                </button>
              ))}
            </div>

            {history.length > 0 && (
              <div className="mt-6" data-testid="exp-history">
                <p className="text-xs text-foreground/40 mb-2.5 flex items-center gap-1.5"><History size={13} /> {ex.history}</p>
                <div className="flex flex-wrap gap-2">
                  {history.map((h, i) => (
                    <button key={i} onClick={() => run(h.topic)} data-testid={`exp-history-item-${i}`}
                      className="text-xs sm:text-sm px-3.5 py-2 rounded-full border border-white/10 text-foreground/60 hover:text-primary hover:bg-primary/[0.06] hover:border-primary/40 transition-colors">
                      {h.topic}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {locked && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="glass rounded-2xl border border-primary/25 p-8 text-center frame-pulse" data-testid="exp-locked">
            <span className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-4"><Lock size={24} strokeWidth={1.6} /></span>
            <p className="font-display text-xl text-foreground/90 mb-2">{ex.locked}</p>
            <p className="text-sm text-foreground/55 leading-relaxed max-w-md mx-auto mb-6">{ex.lockedDesc}</p>
            <button onClick={() => navigate("/tarifs")} data-testid="exp-locked-cta"
              className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
              <Sparkles size={15} /> {ex.lockedCta}
            </button>
          </motion.div>
        )}

        {loading && <AiThinking variant="expedition" skeleton="cards" testId="exp-loading" />}

        <AnimatePresence>
          {data && !loading && (
            <motion.div key={data.topic} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="space-y-7" data-testid="exp-result">
              <div className="text-center">
                <p className="uppercase tracking-[0.25em] text-[0.7rem] text-primary/70 mb-1.5">{ex.topicLabel}</p>
                <p className="font-display italic text-lg sm:text-xl text-foreground/85 leading-snug">{data.topic}</p>
                {data.intro && <p className="text-sm text-foreground/60 leading-relaxed max-w-xl mx-auto mt-2">{data.intro}</p>}
              </div>

              {sections.map((s) => (
                (s.items && s.items.length > 0) ? (
                  <div key={s.kind} data-testid={`exp-section-${s.kind}`}>
                    <p className={`font-display flex items-center gap-2 mb-3 ${s.name}`}>
                      <span className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${s.chip}`}><s.Icon size={15} /></span>
                      {s.label}
                    </p>
                    <div className="grid gap-3">
                      {s.items.map((it, i) => (
                        <a
                          key={i}
                          href={SEARCH_URL[s.kind](it.query || `${it.title} ${it[s.subKey] || ""}`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`exp-${s.kind}-${i}`}
                          className={`group rounded-2xl border p-4 flex items-start gap-3 transition-colors hover:border-primary/40 ${s.cls}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground/90 leading-snug">{it.title}</p>
                            {it[s.subKey] && <p className={`text-xs mt-0.5 ${s.name}`}>{it[s.subKey]}</p>}
                            {it.why && <p className="text-sm text-foreground/60 leading-relaxed mt-1.5">{it.why}</p>}
                          </div>
                          <span className="flex items-center gap-1 text-xs text-foreground/50 group-hover:text-primary transition-colors whitespace-nowrap flex-shrink-0 pt-0.5">
                            {s.cta} <ExternalLink size={13} />
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                ) : null
              ))}

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-1">
                {onExplore && (
                  <button onClick={deepen} data-testid="exp-deepen" className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">
                    <Sparkles size={15} /> {ex.deepen}
                  </button>
                )}
                <button onClick={shareCard} disabled={sharing} data-testid="exp-share" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-primary/90 border border-primary/30 hover:bg-primary/10 transition-colors disabled:opacity-50">
                  {sharing ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} {ex.share}
                </button>
                <button onClick={reset} data-testid="exp-again" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-foreground/60 hover:text-primary border border-white/10 hover:border-primary/30 transition-colors">
                  <RotateCcw size={15} /> {ex.again}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
